<template>
  <div class="p-b-5" data-testid="invites">
    <div
      v-if="!loading"
      class="m-t-10 m-b-15"
    >
      <button
        type="button"
        :disabled="showAddNewForm"
        class="btn btn-success btn-sm"
        @click="showForm"
      >
        <i class="fa fa-plus" />
        <span v-t="'INVITE_USER'" />
      </button>

      <v-select
        v-if="invites.length"
        v-model="inviteFilter"
        class="btn-xl pull-right table-filter-dropdown m-t-0"
        :clearable="false"
        :searchable="false"
        :options="['All', 'Accepted', 'Pending', 'Expired', 'Declined']"
        @input="filterChange"
      />
    </div>

    <div
      v-if="!loading"
      class="invites-container"
    >
      <div
        v-if="!showAddNewForm && emptyInvites"
        v-t="'NO_INVITED_USERS'" 
        class="well well-sm"
      />

      <InviteForm
        v-if="showAddNewForm"
        :existing-users="existingUsersExcludingAccepted"
        @submit="submitNewInvite"
        @cancel="showAddNewForm = false"
      />
      
      <div
        v-if="invites && invites.length && !filteredInvites.length" 
        v-t="emptyListMessage"
        class="invites-sent well well-sm"
      />
      
      <div
        v-for="invite in filteredInvites"
        :key="invite.id"
        class="invites-sent well well-sm"
      >
        <div class="invite-user-details">
          <h2 class="invite-user-name">
            {{ invite.name }}
          </h2>
          <div class="m-t-5 invite-user-email">
            {{ invite.email }}
          </div>
        </div>

        <span
          class="label f-s-11"
          :class="invite.statusClass"
          v-text="invite.status"
        />
        
        <div>
          <a
            v-if="copyUrlSupported" 
            class="btn btn-function"
            :style="{'visibility': invite.url ? 'visible':'hidden'}"
            @click="copyInviteURLToClipboard(invite.url)" 
          >
            <i
              v-tooltip.left="$t('COPY_INVITE_URL')"
              class="typcn typcn-code"
            />
          </a>

          <a
            class="btn btn-function"
            @click="confirmDeleteModal(invite)"
          >
            <i
              v-if="!invite.user"
              v-tooltip.left="$t('DELETE_INVITE')"
              class="typcn typcn-trash"
            />
            <i
              v-if="invite.user"
              v-tooltip.left="$t('DELETE_USER')"
              class="typcn typcn-trash"
            />
          </a>
        </div>
      </div>
    </div>

    <div
      v-if="loading"
      class="well well-sm text-center"
    >
      <Loader />
      <span v-t="'LOADING_INVITES'" />
    </div> 
    
    <modal
      v-model="showModal"
      :title="deleteModalTitle"
      @hide="hideModal"
    >
      <p v-html="deleteModalText" />
      <div slot="footer">
        <button
          v-t="'DELETE'"
          type="button"
          class="btn btn-primary"
          @click="deleteInviteOrUser"
        />
        <button
          v-t="'CANCEL'"
          type="button"
          class="btn btn-danger"
          @click="hideModal"
        />
      </div>
    </modal>
  </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from 'vuex';
import messages from '../locales';
import sharedMessages from '@/core/locales';

import InviteForm from './inviteForm';
import Loader from '@/core/components/Loader';
import { clipboardService } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';
import { STATUS_ERROR, STATUS_SUCCESS, STATUS_PENDING } from '@/store/helpers';
import cloneDeep from 'lodash/cloneDeep';

export default {
  name: 'Invites',
  components: { InviteForm, Loader },
  props:{
    inviteTargetId: {
      type: String,
      required: true
    },
    inviteTargetType: {
      type: String,
      required: true
    },
    existingUsers: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      showAddNewForm: false,
      showModal: false,
      copyUrlSupported: clipboardService.supported,
      inviteFilter: 'All',
      inviteToDelete: null
    };
  },
  computed: {
    ...mapState('invites', {
      allInvites: state => state.invites
    }),
    ...mapGetters('invites', ['getInvitesStatus', 'deleteInviteStatus', 'createInviteStatus']),
    emptyInvites() {
      return this.invites.length == 0;
    },
    loading() {
      return this.getInvitesStatus.status === STATUS_PENDING;
    },
    filteredInvites() {
      const invitesToOrder = this.inviteFilter == 'All' ? this.invites : this.invites.filter(i => i.status === this.inviteFilter);
      return cloneDeep(invitesToOrder).sort(this.sortByName);
    },
    emptyListMessage() {
      switch (this.inviteFilter) {
      case 'Accepted':
        return 'NO_ACCEPTED_INVITES';
      case 'Pending':
        return 'NO_PENDING_INVITES';
      case 'Expired':
        return 'NO_EXPIRED_INVITES';
      case 'Declined':
        return 'NO_DECLINED_INVITES';
      
      default:
        return 'NO_INVITED_USERS';
      }
    },
    deleteModalTitle() {
      return this.inviteToDelete?.status == 'Accepted'
        ? this.$i18n.t('DELETE_MODAL_TITLE_USER')
        : this.$i18n.t('DELETE_MODAL_TITLE_INVITE');
    },
    deleteModalText() {
      return this.inviteToDelete?.status == 'Accepted'
        ? this.$i18n.t('DELETE_MODAL_TEXT_USER', this.inviteToDelete)
        : this.$i18n.t('DELETE_MODAL_TEXT_INVITE', this.inviteToDelete);
    },
    invites() {
      return this.allInvites?.[this.inviteTargetType]?.[this.inviteTargetId] ?? [];
    },
    existingUsersExcludingAccepted() {
      if (this.existingUsers.length) {
        const acceptedInvites = this.invites.filter(i => i.user);
        if (acceptedInvites.length) {
          return this.existingUsers.filter(u => !acceptedInvites.some(i => i.user.id === u.id));
        }
        return this.existingUsers;
      }
      return [];
    }
  },
  async created() {
    await this.getInvites({
      inviteTargetType: this.inviteTargetType,
      inviteTargetId: this.inviteTargetId
    });
  },
  methods:{
    ...mapActions('invites', ['getInvites', 'inviteUser', 'deleteInvite', 'deleteUser']),
    showForm() {
      this.showAddNewForm = true;
    },
    filterChange(value) {
      this.inviteFilter = value;
    },
    confirmDeleteModal(invite) {
      this.inviteToDelete = invite;
      this.showModal = true;
    },
    hideModal() {
      this.showModal = false;
      this.inviteToDelete = null;      
    },
    async deleteInviteOrUser() {
      if (this.inviteToDelete) {
        if (this.inviteToDelete.user) {
          const userToDelete = {
            inviteTargetType: this.inviteTargetType,
            inviteTargetId: this.inviteTargetId,
            invite: this.inviteToDelete
          };
          if (this.$listeners['delete-user']) {
            this.$emit('delete-user', userToDelete);           
          } else {
            await this.deleteUser(userToDelete);
            if (this.deleteInviteStatus.status === STATUS_SUCCESS) {
              utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('DELETE_USER_SUCCESS'));
            }
            if (this.deleteInviteStatus.status === STATUS_ERROR) {
              utilities.showToaster(STATUS_ERROR, this.$i18n.t('DELETE_USER_FAIL'));
            }
          }
        } else {
          await this.deleteInvite({
            inviteTargetType: this.inviteTargetType,
            inviteTargetId: this.inviteTargetId,
            invite: this.inviteToDelete
          });
          if (this.deleteInviteStatus.status === STATUS_SUCCESS) {
            utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('DELETE_INVITE_SUCCESS'));
          }
          if (this.deleteInviteStatus.status === STATUS_ERROR) {
            utilities.showToaster(STATUS_ERROR, this.$i18n.t('DELETE_INVITE_FAIL'));
          }
        }
        
        this.hideModal();
      }
    },
    async submitNewInvite(invite) {
      const existingUser = this.existingUsersExcludingAccepted.find(u => u.email === invite.email);

      if (existingUser) {
        this.$emit('link-existing-user', { inviteTargetId: this.inviteTargetId, ...existingUser });
      }
      else {
        await this.inviteUser({
          inviteTargetType: this.inviteTargetType,
          inviteTargetId: this.inviteTargetId,
          invite
        });
        if (this.createInviteStatus.status === STATUS_SUCCESS) {
          utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('CREATE_INVITE_SUCCESS'));
          
        }
        if (this.createInviteStatus.status === STATUS_ERROR) {
          utilities.showToaster(STATUS_ERROR, this.createInviteStatus.error);
        }
      }

      this.showAddNewForm = false;
      this.$emit('created-new-invite');
    },
    copyInviteURLToClipboard(url) {
      try{
        clipboardService.copyText(url);
        utilities.showToaster(STATUS_SUCCESS, this.$i18n.t('COPY_URL_SUCCESS'));
      } catch {
        utilities.showToaster(STATUS_ERROR, this.$i18n.t('COPY_URL_FAIL'));
      }
    },
    sortByName(a, b) {
      const nameA = a.name.toUpperCase(); // Convert to uppercase for case-insensitive sorting
      const nameB = b.name.toUpperCase();

      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0; // Names are equal
},
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  },
};
</script>

<style lang="scss">
form.user-group {
  .multi-select-lg {
    width: 100%;
  }

  .v-select .vs__selected {
    height: 32px;
    padding: 8px 8px 7px 10px;
    border-bottom: 1px solid transparent;
  }
}
</style>